import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Typography, Paper, Box } from "@mui/material";
import { v4 as uuid } from "uuid";
import { Formik } from "formik";
import * as Yup from "yup";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import CustomTextArea from "../../app/common/form/CustomTextArea";
import { ActivityFormValues } from "../../app/models/activity";
import TabContainer, { TabData } from "../../app/common/TabContainer";
import ParticipantsTable from "../participants/ParticipantsTable";
import CustomDateInput from "../../app/common/form/CustomDateInput";
import { useTranslation } from "react-i18next";

export default function ActivityForm() {
  const { t } = useTranslation();
  const [activity, setActivity] = useState<ActivityFormValues>(
    new ActivityFormValues()
  );
  const loadActivity = useStore((s) => s.activityStore.loadActivity);
  const activityStore = useStore((s) => s.activityStore);
  const userStore = useStore((s) => s.userStore);
  const { createActivity, updateActivity, loadingInitial } =
    activityStore;
  const navigate = useNavigate();
  const { id } = useParams();
  const commonRequiredMessage = t("common.requiredMessage");
  const validationSchema = Yup.object({
    name: Yup.string().required(
      `${t("activityForm.nameLabel")} ${commonRequiredMessage}`
    ),
  });

  useEffect(() => {
    if (id) {
      loadActivity(id).then((a: any) => setActivity(new ActivityFormValues(a)));
    }
  }, [id, loadActivity]);

  const handleFormSubmit = (values: ActivityFormValues) => {
    if (!values.id) {
      const newActivity = {
        ...values,
        id: uuid(),
        userId: userStore.loggedUser?.id!,
      };
      createActivity(newActivity).then(() => navigate(`/activities`));
    } else {
      updateActivity(values).then(() => navigate(`/activities`));
    }
  };

  const tabs: TabData[] = [
    {
      menuItem: t("activityForm.participants"),
      render: () => <ParticipantsTable />,
    },
  ];

  if (loadingInitial) return <LoadingComponent content={t("common.loading")} />;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {activity?.id ? (
          <Typography variant="h4" component="h2">
            {`${activity.name} - ${t("activityForm.editActivityHeader")}`}
          </Typography>
        ) : null}
      </Box >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{
            ...activity,
            createdByUser: activity.createdByUser?.username as any,
          }}
          onSubmit={(values) => handleFormSubmit(values)}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("general.basicData")}
              </Typography>
              <div className={activity.id ? "two fields" : "one field"}>
                <CustomTextInput
                  name="name"
                  placeholder={t("activityForm.namePlaceholder")}
                  label={t("activityForm.nameLabel")}
                />

                {activity.id && (
                  <CustomDateInput
                    name="createdAt"
                    value={activity.createdAt!}
                    label={t("activityForm.createdAtLabel")}
                    placeholderText={t("activityForm.createdAtLabel")}
                    dateFormat="dd.MM.yyyy"
                  />
                )}
              </div>

              <CustomTextArea
                rows={3}
                name="note"
                placeholder={t("activityForm.notePlaceholder")}
                label={t("activityForm.noteLabel")}
              />

              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty || !isValid}
                type="submit"
                sx={{ float: "right", mt: 2, ml: 2 }}
              >
                {t("general.confirm")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                component={Link}
                to="/activities"
                type="button"
                sx={{ float: "right", mt: 2 }}
              >
                {t("general.cancel")}
              </Button>
            </form>
          )}
        </Formik>
      </Paper>

      {
        id && (
          <Paper sx={{ p: 2 }}>
            <TabContainer tabs={tabs} />
          </Paper>
        )
      }
    </>
  );
}
